import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';

let panelInstances = 0;

/**
 * The icon + mat-menu half of the find & replace feature. It is created
 * dynamically by FindReplaceDirective and knows nothing about the textarea it
 * belongs to — it only reports what the user asked for and renders the match
 * state the directive hands back.
 */
@Component({
  selector: 'app-find-replace-panel',
  templateUrl: './find-replace-panel.component.html'
})
export class FindReplacePanelComponent {
  @HostBinding('class.app-find-replace-trigger') readonly hostClass = true;

  @Output() findChange = new EventEmitter<string>();
  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();
  @Output() replaceOne = new EventEmitter<void>();
  @Output() replaceAll = new EventEmitter<void>();
  /** Replace across every field of the surrounding scope, not just this one. */
  @Output() replaceEverywhere = new EventEmitter<void>();
  @Output() undo = new EventEmitter<void>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  tooltip = 'Find & replace';
  /** False while the textarea is disabled or read-only — find still works. */
  canReplace = true;
  /** Driven by the directive; false once the note no longer matches what we wrote. */
  canUndo = false;
  /** True while the pending undo would roll back a whole-scope replace. */
  scopeUndo = false;
  /** True only when the textarea sits inside a [appFindReplaceScope]. */
  hasScope = false;
  /** Matches across the whole scope, this field included. */
  scopeMatchCount = 0;

  find = '';
  replace = '';
  matchCount = 0;
  activeIndex = -1;

  /** Lets the directive reach into the CDK overlay to focus the find field. */
  readonly panelClass = `app-fr-panel-${++panelInstances}`;

  @ViewChild(MatMenuTrigger) private trigger?: MatMenuTrigger;
  @ViewChild('findInput') private findInput?: ElementRef<HTMLInputElement>;

  constructor(private cdr: ChangeDetectorRef) {}

  get countLabel(): string {
    if (!this.find) {
      return '';
    }
    return this.matchCount ? `${this.activeIndex + 1}/${this.matchCount}` : 'None';
  }

  get hasMatches(): boolean {
    return this.matchCount > 0;
  }

  open(seed?: string): void {
    if (seed !== undefined) {
      this.setFind(seed);
    }
    if (this.trigger && !this.trigger.menuOpen) {
      this.trigger.openMenu();
    }
  }

  setFind(value: string): void {
    if (this.find === value) {
      return;
    }
    this.find = value;
    this.cdr.markForCheck();
    this.findChange.emit(value);
  }

  setMatchState(matchCount: number, activeIndex: number): void {
    this.matchCount = matchCount;
    this.activeIndex = activeIndex;
    this.cdr.markForCheck();
  }

  setScopeState(hasScope: boolean, scopeMatchCount: number): void {
    if (this.hasScope === hasScope && this.scopeMatchCount === scopeMatchCount) {
      return;
    }
    this.hasScope = hasScope;
    this.scopeMatchCount = scopeMatchCount;
    this.cdr.markForCheck();
  }

  get hasScopeMatches(): boolean {
    return this.scopeMatchCount > 0;
  }

  /** Undo covers every field once a scope replace has run. */
  get undoTooltip(): string {
    return this.scopeUndo ? 'Undo the last replace across all fields' : 'Undo the last replace in this field';
  }

  /** Label carries the blast radius so it is visible before the dialog opens. */
  get scopeLabel(): string {
    return this.scopeMatchCount ? `All fields (${this.scopeMatchCount})` : 'All fields';
  }

  setCanUndo(canUndo: boolean, scopeUndo = false): void {
    if (this.canUndo === canUndo && this.scopeUndo === scopeUndo) {
      return;
    }
    this.canUndo = canUndo;
    this.scopeUndo = scopeUndo;
    this.cdr.markForCheck();
  }

  onFindInput(event: Event): void {
    this.find = (event.target as HTMLInputElement).value;
    this.findChange.emit(this.find);
  }

  onReplaceInput(event: Event): void {
    // Kept verbatim — no trim, so a replacement of ", " or a trailing space works.
    this.replace = (event.target as HTMLInputElement).value;
  }

  onMenuOpened(): void {
    this.opened.emit();
    this.focusFind();
  }

  onMenuClosed(): void {
    this.closed.emit();
  }

  /**
   * MatMenu's key manager owns arrows, Home/End, Tab and typeahead for its
   * items. This panel has no menu items, so every key except Escape (which
   * should still close the menu) belongs to the inputs.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      return;
    }
    event.stopPropagation();

    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();

    const target = event.target as HTMLElement | null;
    if (target?.classList.contains('app-fr-replace')) {
      if (this.canReplace) {
        this.replaceOne.emit();
      }
    } else if (event.shiftKey) {
      this.previous.emit();
    } else {
      this.next.emit();
    }
  }

  private focusFind(): void {
    setTimeout(() => {
      // The menu body is rendered lazily into the CDK overlay, so the ViewChild
      // can miss it on the first open; the panel class is the reliable handle.
      const input =
        this.findInput?.nativeElement ??
        (document.querySelector(`.${this.panelClass} .app-fr-find`) as HTMLInputElement | null);
      input?.focus();
      input?.select();
    });
  }
}
